import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { mixpanelEvent } from "@/lib/mixpanels/mixpanel";
import { cn } from "@/lib/utils";
import useWindowDimensions from "@/lib/web-ui/useWindowDimensions";
import { Icon, InteractionLayer, Typography } from "@bookips/sds";
import Autoplay from "embla-carousel-autoplay";
import useEmblaCarousel from "embla-carousel-react";
import Image from "next/image";
import Link from "next/link";
import { useCallback, useEffect, useState } from "react";
const AUTOPLAY_DELAY = 6000;
const HomeBanner = (param)=>{
    let { items , homeId  } = param;
    const [emblaRef, emblaApi] = useEmblaCarousel({
        loop: true
    }, [
        Autoplay({
            delay: AUTOPLAY_DELAY,
            stopOnInteraction: false
        })
    ]);
    const [selectedIndex, setSelectedIndex] = useState(0);
    const { isMobile  } = useWindowDimensions();
    const scrollTo = useCallback((index)=>{
        if (emblaApi) {
            emblaApi.scrollTo(index);
            const autoplay = emblaApi.plugins().autoplay;
            if (autoplay) autoplay.reset();
        }
    }, [
        emblaApi
    ]);
    const scrollPrev = useCallback(()=>{
        emblaApi === null || emblaApi === void 0 ? void 0 : emblaApi.scrollPrev();
        // MX-371: 배너 인디케이터 클릭 트래킹
        mixpanelEvent("Banner Indicator Clicked", {
            "Home Id": homeId,
            Type: "인디케이터 버튼",
            Direction: "prev"
        });
    }, [
        emblaApi,
        homeId
    ]);
    const scrollNext = useCallback(()=>{
        emblaApi === null || emblaApi === void 0 ? void 0 : emblaApi.scrollNext();
        // MX-371: 배너 인디케이터 클릭 트래킹
        mixpanelEvent("Banner Indicator Clicked", {
            "Home Id": homeId,
            Type: "인디케이터 버튼",
            Direction: "next"
        });
    }, [
        emblaApi,
        homeId
    ]);
    // 현재 선택된 슬라이드 번호를 가져오기 위한 함수
    const onSelect = useCallback((api)=>{
        setSelectedIndex(api.selectedScrollSnap());
    }, []);
    // 탭 전환 등으로 items가 변경되면 첫 슬라이드로 리셋하고 autoplay 재시작
    useEffect(()=>{
        if (!emblaApi) return;
        emblaApi.scrollTo(0, true);
        const autoplay = emblaApi.plugins().autoplay;
        if (autoplay) autoplay.reset();
    }, [
        emblaApi,
        items
    ]);
    useEffect(()=>{
        if (!emblaApi) return;
        onSelect(emblaApi);
        emblaApi.on("select", onSelect);
        emblaApi.on("reInit", onSelect);
    }, [
        emblaApi,
        onSelect
    ]);
    // MX-370: 메인배너 클릭 트래킹
    const handleBannerClick = (slide, index)=>{
        mixpanelEvent("[Home] Main Banner Clicked", {
            "Home Id": homeId,
            Position: "쏠북 홈",
            Title: slide.title,
            Link: slide.link,
            Seq: index + 1
        });
    };
    return /*#__PURE__*/ _jsx("div", {
        className: "my-10 max-lg:my-8 max-lg:px-8 max-sm:my-6 max-sm:px-5",
        children: /*#__PURE__*/ _jsxs("div", {
            className: "relative mx-auto flex w-full max-w-[1200px] overflow-hidden rounded-lg bg-white max-sm:my-5 max-sm:rounded-md",
            children: [
                /*#__PURE__*/ _jsx("div", {
                    className: cn("absolute top-0 bottom-0 left-0 z-10 w-[240px] flex-col overflow-y-auto rounded-l-lg border border-gray-100 bg-white", "flex max-lg:w-[200px] max-md:hidden", "[&::-webkit-scrollbar]:w-1 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-gray-900/20"),
                    children: items === null || items === void 0 ? void 0 : items.map((slide, index)=>{
                        const isSelected = selectedIndex === index;
                        return /*#__PURE__*/ _jsxs("button", {
                            onClick: ()=>scrollTo(index),
                            className: cn("relative flex items-center p-4 text-left text-sm transition-colors", isSelected ? "bg-gray-50 font-medium text-gray-900" : "text-gray-600 hover:bg-gray-50", "max-md:hidden", "border-b border-gray-100"),
                            children: [
                                isSelected && /*#__PURE__*/ _jsx("div", {
                                    className: "absolute inset-y-0 left-0 animate-[progress_linear_forwards] bg-gray-200",
                                    style: {
                                        animationDuration: "".concat(AUTOPLAY_DELAY, "ms")
                                    }
                                }, "".concat(slide.id, "-").concat(index)),
                                /*#__PURE__*/ _jsx("span", {
                                    className: "relative z-10",
                                    children: slide.title
                                })
                            ]
                        }, slide.id);
                    })
                }),
                /*#__PURE__*/ _jsxs("div", {
                    className: "relative ml-[240px] flex-1 overflow-hidden max-lg:ml-[200px] max-md:ml-0",
                    children: [
                        /*#__PURE__*/ _jsx("div", {
                            ref: emblaRef,
                            className: "overflow-hidden",
                            children: /*#__PURE__*/ _jsx("div", {
                                className: "flex",
                                children: items === null || items === void 0 ? void 0 : items.map((slide, index)=>/*#__PURE__*/ _jsx("div", {
                                        className: "relative min-w-0 flex-[0_0_100%]",
                                        children: /*#__PURE__*/ _jsx("div", {
                                            className: cn("flex w-full items-center justify-between", "aspect-[8/3]", "max-sm:aspect-[8/5]"),
                                            children: /*#__PURE__*/ _jsx("div", {
                                                className: "relative h-full w-full",
                                                children: (isMobile ? slide.imageUrlMobile : slide.imageUrlPc) && /*#__PURE__*/ _jsx(Link, {
                                                    href: slide.link,
                                                    onClick: ()=>handleBannerClick(slide, index),
                                                    children: /*#__PURE__*/ _jsx(Image, {
                                                        src: isMobile ? slide.imageUrlMobile : slide.imageUrlPc,
                                                        alt: slide.title,
                                                        fill: true,
                                                        className: "object-cover"
                                                    })
                                                })
                                            })
                                        })
                                    }, slide.id))
                            })
                        }),
                        /*#__PURE__*/ _jsxs("div", {
                            className: cn("absolute right-6 bottom-6 z-10 flex items-center gap-0.5 overflow-hidden rounded-full bg-gray-900/50", "max-lg:right-5 max-lg:bottom-5 max-lg:px-2.5", "max-sm:right-auto max-sm:bottom-0 max-sm:left-0", "max-sm:rounded-none max-sm:rounded-tr-md max-sm:px-2", "max-lg:py-0.5 max-sm:py-0"),
                            children: [
                                /*#__PURE__*/ _jsx(InteractionLayer, {
                                    variant: "default",
                                    className: "py-1 pl-1.5 max-lg:hidden",
                                    onClick: scrollPrev,
                                    children: /*#__PURE__*/ _jsx("div", {
                                        className: "flex h-4 w-4 items-center justify-center text-white hover:text-white/80",
                                        children: /*#__PURE__*/ _jsx(Icon.ArrowLeft_1px, {
                                            color: "white"
                                        })
                                    })
                                }),
                                /*#__PURE__*/ _jsxs(Typography, {
                                    variant: "subtitle2",
                                    className: "max-sm:typo-label flex items-center gap-0.5 text-center text-white",
                                    children: [
                                        /*#__PURE__*/ _jsx("span", {
                                            children: selectedIndex + 1
                                        }),
                                        /*#__PURE__*/ _jsx("span", {
                                            children: "/"
                                        }),
                                        /*#__PURE__*/ _jsx("span", {
                                            children: items === null || items === void 0 ? void 0 : items.length
                                        })
                                    ]
                                }),
                                /*#__PURE__*/ _jsx(InteractionLayer, {
                                    variant: "default",
                                    className: "py-1 pr-1.5 max-lg:hidden",
                                    onClick: scrollNext,
                                    children: /*#__PURE__*/ _jsx("div", {
                                        className: "flex h-4 w-4 items-center justify-center text-white hover:text-white/80",
                                        children: /*#__PURE__*/ _jsx(Icon.ArrowRight_1px, {
                                            color: "white"
                                        })
                                    })
                                })
                            ]
                        })
                    ]
                })
            ]
        })
    });
};
export default HomeBanner;
